import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR, formatDate, todayISO } from "@/lib/utils.helpers";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TYPE_META = {
  advance: { label: "Advance", badge: "bg-orange-100 text-orange-800" },
  bonus: { label: "Bonus", badge: "bg-green-100 text-green-800" },
  deduction: { label: "Deduction", badge: "bg-red-100 text-red-800" },
  payment: { label: "Payment", badge: "bg-blue-100 text-blue-800" },
};

export default function Transactions() {
  const [tab, setTab] = useState("advance");
  const [employees, setEmployees] = useState([]);
  const [txs, setTxs] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ employee_id: "", amount: "", date: todayISO(), notes: "" });

  const load = async () => {
    const [empRes, txRes] = await Promise.all([
      api.get("/employees"),
      api.get(`/transactions?type=${tab}`),
    ]);
    setEmployees(empRes.data);
    setTxs(txRes.data);
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return toast.error("Select an employee");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post("/transactions", {
        employee_id: form.employee_id,
        amount: Number(form.amount),
        date: form.date,
        type: tab,
        notes: form.notes,
      });
      toast.success(`${TYPE_META[tab].label} added`);
      setForm({ employee_id: "", amount: "", date: todayISO(), notes: "" });
      load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    try { await api.delete(`/transactions/${id}`); toast.success("Removed"); load(); } catch { toast.error("Delete failed"); }
  };

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";
  const filtered = txs.filter((t) => !q || empName(t.employee_id).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold">Payroll Adjustments</div>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">Advances, Bonus, Deductions & Payments</h1>
        <p className="text-slate-600 mt-1">Track every transaction against employees. All entries feed into payroll automatically.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E5E2DC]" data-testid="tx-tabs">
          <TabsTrigger value="advance" data-testid="tab-advance">Advances</TabsTrigger>
          <TabsTrigger value="bonus" data-testid="tab-bonus">Bonus</TabsTrigger>
          <TabsTrigger value="deduction" data-testid="tab-deduction">Deductions</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment">Payments</TabsTrigger>
        </TabsList>

        {["advance", "bonus", "deduction", "payment"].map((t) => (
          <TabsContent key={t} value={t} className="mt-4 space-y-4">
            <Card className="border-[#E5E2DC] p-6">
              <h3 className="font-serif text-xl text-slate-900 mb-4">Add {TYPE_META[t].label}</h3>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div className="md:col-span-2 space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Employee</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger data-testid="tx-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Amount (₹)</Label>
                  <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="tx-amount-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="tx-date-input" />
                </div>
                <div className="md:col-span-5 space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Notes</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="tx-notes-input" />
                </div>
                <div className="md:col-span-5 flex justify-end">
                  <Button type="submit" data-testid="tx-submit-button" className="bg-[#111111] hover:bg-[#C59B27] transition-colors text-white">
                    <Plus className="w-4 h-4 mr-1" /> Add {TYPE_META[t].label}
                  </Button>
                </div>
              </form>
            </Card>

            <Card className="border-[#E5E2DC] p-4">
              <div className="relative max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Filter by employee..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="tx-search-input" />
              </div>
            </Card>

            <Card className="border-[#E5E2DC] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#FDFDF9]">
                    <TableHead className="uppercase text-[10px] font-bold text-slate-500">Employee</TableHead>
                    <TableHead className="uppercase text-[10px] font-bold text-slate-500">Type</TableHead>
                    <TableHead className="uppercase text-[10px] font-bold text-slate-500">Date</TableHead>
                    <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">Amount</TableHead>
                    <TableHead className="uppercase text-[10px] font-bold text-slate-500">Notes</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No {TYPE_META[t].label.toLowerCase()} entries yet.</TableCell></TableRow>
                  ) : filtered.map((tx) => (
                    <TableRow key={tx.id} className="hover:bg-slate-50" data-testid={`tx-row-${tx.id}`}>
                      <TableCell className="font-medium">{empName(tx.employee_id)}</TableCell>
                      <TableCell><Badge className={TYPE_META[tx.type].badge}>{TYPE_META[tx.type].label}</Badge></TableCell>
                      <TableCell className="text-sm">{formatDate(tx.date)}</TableCell>
                      <TableCell className="text-right font-medium">{formatINR(tx.amount)}</TableCell>
                      <TableCell className="text-sm text-slate-600">{tx.notes}</TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-red-600" data-testid={`tx-delete-${tx.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(tx.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
